/**
 * Pure helpers for site-wide search. Kept free of Payload/Next imports so the
 * URL-building and ranking rules are unit-testable without a live CMS.
 */

export const MIN_QUERY_LENGTH = 2;

export type SiteSearchKind = 'page' | 'post' | 'doc' | 'glossary';

export interface SiteSearchResult {
  title: string;
  url: string;
  kind: SiteSearchKind;
  /** Human label for the result's section, shown as a badge. */
  label: string;
  excerpt: string;
}

interface RawDoc {
  title?: unknown;
  slug?: unknown;
  excerpt?: unknown;
  category?: unknown;
  /** Glossary stores its label as `term`, not `title`. */
  term?: unknown;
  /** Glossary stores its summary as `description`, not `excerpt`. */
  description?: unknown;
}

/** Trim an excerpt to a single readable line. */
function tidyExcerpt(value: unknown, max = 140): string {
  if (typeof value !== 'string') return '';
  const clean = value.replace(/\s+/g, ' ').trim();
  return clean.length > max ? `${clean.slice(0, max - 1).trimEnd()}…` : clean;
}

function isUsable(d: RawDoc): d is RawDoc & { title: string; slug: string } {
  return typeof d?.title === 'string' && !!d.title && typeof d?.slug === 'string' && !!d.slug;
}

/**
 * Flatten per-collection results into one ranked list.
 *
 * Order is deliberate: pages (products/industries) first because a broken URL is
 * most often a moved marketing page, then help articles, then posts, then
 * glossary terms.
 */
export function buildSiteSearchResults(input: {
  pages?: RawDoc[];
  posts?: RawDoc[];
  docs?: RawDoc[];
  glossary?: RawDoc[];
}): SiteSearchResult[] {
  const out: SiteSearchResult[] = [];

  for (const d of input.pages ?? []) {
    if (!isUsable(d)) continue;
    out.push({
      title: d.title,
      // The `home` page is the CMS source of `/`; `/home` itself 301s there
      // (see next.config.ts) and is excluded from the sitemap. Link straight to
      // the canonical URL rather than sending searchers through a redirect.
      url: d.slug === 'home' ? '/' : `/${d.slug}`,
      kind: 'page',
      label: 'Page',
      excerpt: tidyExcerpt(d.excerpt),
    });
  }

  for (const d of input.docs ?? []) {
    if (!isUsable(d)) continue;
    // A help article's URL requires its category; skip it rather than emit a broken link.
    const cat = d.category;
    const categorySlug =
      cat && typeof cat === 'object' && 'slug' in cat ? (cat as { slug?: unknown }).slug : undefined;
    if (typeof categorySlug !== 'string' || !categorySlug) continue;
    out.push({
      title: d.title,
      url: `/help/${categorySlug}/${d.slug}`,
      kind: 'doc',
      label: 'Help',
      excerpt: tidyExcerpt(d.excerpt),
    });
  }

  for (const d of input.posts ?? []) {
    if (!isUsable(d)) continue;
    out.push({
      title: d.title,
      url: `/blog/${d.slug}`,
      kind: 'post',
      label: 'Blog',
      excerpt: tidyExcerpt(d.excerpt),
    });
  }

  for (const d of input.glossary ?? []) {
    // Glossary has NO `title`/`excerpt` field — it uses `term`/`description`.
    // Querying or reading `title` here silently yielded nothing (and, queried
    // server-side, made Payload reject the whole search).
    const term = typeof d?.term === 'string' ? d.term : typeof d?.title === 'string' ? d.title : '';
    if (!term || typeof d?.slug !== 'string' || !d.slug) continue;
    out.push({
      title: term,
      url: `/glossary/${d.slug}`,
      kind: 'glossary',
      label: 'Glossary',
      excerpt: tidyExcerpt(d.description ?? d.excerpt),
    });
  }

  return out;
}
